import React, { useEffect, useState } from "react";
import { MapPin, ChevronDown, Loader2, AlertCircle, ArrowRight, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApp } from "@/context/AppContext";
import { reverseGeocode, formatApiError, areaSearch, fetchPincodeInfo } from "@/lib/api";
import { toast } from "sonner";
import { setCustomerGeo, clearCustomerGeo } from "@/lib/customerGeo";

const PINCODES = [
  { code: "110001", label: "Connaught Place, Delhi" },
  { code: "110002", label: "Daryaganj, Delhi" },
  { code: "110005", label: "Karol Bagh, Delhi" },
  { code: "110016", label: "Hauz Khas, Delhi" },
  { code: "400001", label: "Fort, Mumbai" },
  { code: "400050", label: "Bandra West, Mumbai" },
  { code: "400058", label: "Andheri West, Mumbai" },
  { code: "560001", label: "MG Road, Bengaluru" },
  { code: "560034", label: "Koramangala, Bengaluru" },
  { code: "560095", label: "HSR Layout, Bengaluru" },
  { code: "600001", label: "Parrys, Chennai" },
  { code: "411001", label: "Camp, Pune" },
  { code: "700001", label: "BBD Bagh, Kolkata" },
  { code: "500001", label: "Afzal Gunj, Hyderabad" },
];

const RECENT_KEY = "vb.recentAreas";
// Session-scoped like the browsing PIN itself (no persistent PIN storage on the device).
const loadRecent = () => { try { return JSON.parse(sessionStorage.getItem(RECENT_KEY) || "[]").slice(0, 3); } catch { return []; } };
export const saveRecentArea = (r) => {
  const entry = { name: r.name, pincode: r.pincode, district: r.district || "", state: r.state || "" };
  const next = [entry, ...loadRecent().filter((x) => !(x.pincode === entry.pincode && x.name === entry.name))].slice(0, 3);
  try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage blocked: recent list is optional */ }
};

function AreaSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [recent] = useState(loadRecent);
  const [busyRecent, setBusyRecent] = useState(null);
  const pickRecent = async (r) => {
    setBusyRecent(r.pincode);
    try {
      const data = await areaSearch([r.name, r.district].filter(Boolean).join(", "));
      const hit = (data.results || []).find((x) => x.pincode === r.pincode);
      onPick(hit || { ...r, serviceable: false }, data.nearest_serviceable || []);
    } catch {
      onPick({ ...r, serviceable: false }, []);
    } finally { setBusyRecent(null); }
  };
  const [state, setState] = useState({ loading: false, data: null, error: false });
  useEffect(() => {
    const term = q.trim().replace(/\s+/g, " ");
    if (term.length < 3) { setState({ loading: false, data: null, error: false }); return undefined; }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    const id = setTimeout(() => {
      areaSearch(term)
        .then((data) => alive && setState({ loading: false, data, error: false }))
        .catch(() => alive && setState({ loading: false, data: null, error: true }));
    }, 400);
    return () => { alive = false; clearTimeout(id); };
  }, [q]);
  const results = state.data?.results || [];
  return (
    <div data-testid="area-search">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Area / Locality / City search</label>
      <div className="mt-1.5 flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 focus-within:border-emerald-500">
        <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Lanka, Varanasi"
          onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
          className="flex-1 min-w-0 py-2 text-sm outline-none" data-testid="area-search-input"
        />
        {state.loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 flex-shrink-0" />}
      </div>
      {q.trim().length < 3 && recent.length > 0 && (
        <div className="mt-1.5" data-testid="recent-areas">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Recent Areas</div>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((r) => (
              <button
                key={`${r.name}-${r.pincode}`} onClick={() => pickRecent(r)} disabled={busyRecent === r.pincode}
                className="max-w-full inline-flex items-center gap-1 rounded-full border border-slate-200 hover:border-emerald-500 bg-white px-2.5 py-1.5 text-xs text-slate-700 disabled:opacity-60"
                data-testid={`recent-area-${r.pincode}`}
              >
                {busyRecent === r.pincode ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3 text-emerald-700 flex-shrink-0" />}
                <span className="truncate">{r.name} — {r.pincode}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {!state.loading && (state.error || (state.data && results.length === 0)) && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-1.5" data-testid="area-search-empty">
          Area nahi mila. PIN code manually enter karein.
        </div>
      )}
      {results.length > 0 && (
        <div className="mt-1.5 rounded-lg border border-slate-100 max-h-48 overflow-y-auto" data-testid="area-search-results">
          {results.length > 1 && <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 pt-2">Sahi area chunein</div>}
          {results.map((r) => (
            <button
              key={`${r.name}-${r.pincode}`} onClick={() => onPick(r, state.data.nearest_serviceable || [])}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-emerald-50"
              data-testid={`area-result-${r.pincode}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{r.name} — {r.pincode}</div>
                <div className="text-[11px] text-slate-500 truncate">{[r.district, r.state].filter(Boolean).join(", ")}</div>
              </div>

            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PincodeSelector({ variant = "inline", trigger = null }) {
  const { pincode, setPincode, pincodeArea } = useApp();
  const [regArea, setRegArea] = useState(null);
  useEffect(() => {
    setRegArea(null);
    if (!pincode || pincodeArea) return undefined;
    let alive = true;
    fetchPincodeInfo(pincode).then((d) => alive && setRegArea(d?.area || null)).catch(() => {});
    return () => { alive = false; };
  }, [pincode, pincodeArea]);
  const shownArea = pincodeArea || regArea;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(pincode);
  const [detecting, setDetecting] = useState(false);
  const [warmSuggestions, setWarmSuggestions] = useState(null); // {detectedPin, locality, options[]}

  const current = PINCODES.find((p) => p.code === pincode);

  const applyPincode = (code, silent = false, area = null) => {
    clearCustomerGeo(); // manual / suggested PIN => PIN-based distance
    if (!/^\d{6}$/.test(code)) {
      toast.error("Please enter a valid 6-digit pincode");
      return false;
    }
    setPincode(code, area);
    setInput(code);
    setWarmSuggestions(null);
    setOpen(false);
    if (!silent) toast.success(`Delivering to ${code}`);
    return true;
  };

  // Manual entry: any PIN the backend can verify (registry or India Post lookup).
  const [checking, setChecking] = useState(false);
  const applyManual = async () => {
    if (!/^\d{6}$/.test(input)) { toast.error("Please enter a valid 6-digit pincode"); return; }
    if (checking) return;
    setChecking(true);
    try {
      const info = await fetchPincodeInfo(input);
      applyPincode(input, false, info?.area || null);
    } catch (e) {
      if (e?.response?.status === 404) toast.error(`PIN ${input} verify nahi ho paya. Sahi PIN ya area ka naam daalein.`);
      else toast.error("Could not verify this PIN. Please check your connection and try again.");
    } finally { setChecking(false); }
  };

  const detectLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Location not supported on this device. Enter PIN manually.");
      return;
    }
    setDetecting(true);
    setWarmSuggestions(null);
    const toastId = toast.loading("Detecting your location...");
    const opts = { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 };

    const onFail = (msg) => {
      toast.error(msg, { id: toastId });
      setDetecting(false);
    };

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await reverseGeocode(latitude, longitude);
          if (!res?.pincode || !/^\d{6}$/.test(res.pincode)) {
            throw new Error("No valid PIN returned for your location");
          }
          const known = res.serviceable || (await fetchPincodeInfo(res.pincode).catch(() => null));
          if (known) {
            setCustomerGeo(res.pincode, latitude, longitude);
            setPincode(res.pincode);
            setInput(res.pincode);
            setOpen(false);
            toast.success(`Detected PIN ${res.pincode}${res.locality ? ` · ${res.locality}` : ""}`, { id: toastId, duration: 4000 });
          } else {
            // Show warm suggestions inside the popover
            setWarmSuggestions({
              detectedPin: res.pincode,
              locality: res.locality || "",
              options: res.nearest_serviceable || [],
            });
            setOpen(true);
            toast.warning(`${res.pincode} not yet serviceable — pick a nearby area.`, { id: toastId, duration: 4000 });
          }
        } catch (err) {
          onFail(formatApiError(err));
        } finally {
          setDetecting(false);
        }
      },
      (err) => {
        const msgs = {
          1: "Location permission denied. Please allow access or enter PIN manually.",
          2: "Location unavailable. Check GPS/network and try again.",
          3: "Location request timed out. Please try again.",
        };
        onFail(msgs[err.code] || "Could not detect your location.");
      },
      opts,
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || <button
          className={variant === "block"
            ? "w-full flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5"
            : "flex items-center gap-1.5 pl-2 pr-1 text-sm text-slate-700 hover:text-emerald-700"}
          data-testid="pincode-selector-btn"
        >
          <MapPin className="w-4 h-4 text-emerald-700 flex-shrink-0" />
          <div className={variant === "block" ? "text-left flex-1" : ""}>
            <div className="text-xs text-slate-500 leading-tight">{variant === "block" ? "Delivering to" : ""}</div>
            <div className="text-sm font-semibold text-slate-900 leading-tight" data-testid="current-pincode">{pincode ? <span className={`inline-block truncate align-bottom ${variant === "block" ? "max-w-[9rem]" : "max-w-[4.5rem]"} sm:max-w-[14rem]`}>{shownArea ? `${shownArea} · ${pincode}` : pincode}</span> : <span className="text-slate-400 font-medium">PIN chunein</span>}</div>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>}
      </PopoverTrigger>
      <PopoverContent className="w-80 max-w-[calc(100vw-1rem)] p-4 max-h-[80vh] overflow-y-auto" align="start" collisionPadding={8}>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Enter your pincode</label>
            <div className="flex gap-2 mt-1.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyManual(); } }}
                placeholder="6-digit pincode"
                className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500"
                data-testid="pincode-input"
              />
              <button onClick={applyManual} disabled={checking} aria-busy={checking} className="px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg disabled:opacity-60" data-testid="pincode-apply">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </button>
            </div>
          </div>
          <AreaSearch
            onPick={async (r) => {
              if (!r.serviceable && !(await fetchPincodeInfo(r.pincode).catch(() => null))) { toast.error(`PIN ${r.pincode} verify nahi ho paya. Doosra area chunein.`); return; }
              saveRecentArea(r); applyPincode(r.pincode, false, r.name.replace(/\s*\(.*?\)/g, "").trim());
            }}
          />
          <button onClick={detectLocation} disabled={detecting} className="w-full flex items-center justify-center gap-2 py-2 border border-emerald-200 text-emerald-800 rounded-lg text-sm font-semibold hover:bg-emerald-50 disabled:opacity-60 disabled:cursor-wait" data-testid="detect-location-btn">
            {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            {detecting ? "Detecting..." : "Detect my location"}
          </button>
          {pincode && (
            <button onClick={() => { clearCustomerGeo(); setPincode(""); setInput(""); setOpen(false); toast.success("PIN hata diya gaya"); }} className="w-full py-2 text-xs font-semibold text-slate-600 hover:text-rose-700 rounded-lg hover:bg-rose-50" data-testid="pincode-clear">
              PIN hataayein ({pincode})
            </button>
          )}

          {warmSuggestions && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="warm-suggestions">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-amber-900">Your area is not currently serviceable</div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    {warmSuggestions.manual ? "Entered PIN" : "Detected PIN"} <span className="font-mono font-bold">{warmSuggestions.detectedPin}</span>
                    {warmSuggestions.locality ? ` · ${warmSuggestions.locality}` : ""}
                  </div>
                </div>
              </div>
              {warmSuggestions.options?.length ? (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-amber-900/80 font-semibold mt-3 mb-1">Nearby serviceable areas</div>
                  <div className="space-y-1">
                    {warmSuggestions.options.map((opt) => (
                      <button
                        key={opt.pincode}
                        onClick={() => applyPincode(opt.pincode)}
                        className="w-full flex items-center justify-between gap-2 bg-white hover:bg-emerald-50 border border-white hover:border-emerald-200 rounded-lg px-3 py-2 text-left"
                        data-testid={`warm-suggestion-${opt.pincode}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{opt.pincode} · {opt.area}</div>
                          <div className="text-[11px] text-slate-500 truncate">{opt.city}, {opt.state} · {opt.shop_count} shop{opt.shop_count === 1 ? "" : "s"}</div>
                        </div>
                        <div className="flex items-center gap-1 text-xs font-semibold text-emerald-700 flex-shrink-0">
                          {opt.distance_km} km <ArrowRight className="w-3 h-3" />
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-xs text-amber-800 mt-2">No nearby serviceable PINs found. Enter one manually above.</div>
              )}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Popular areas</div>
            <div className="max-h-52 overflow-y-auto space-y-1">
              {PINCODES.map((p) => (
                <button
                  key={p.code}
                  onClick={() => applyPincode(p.code)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-emerald-50 flex items-center justify-between ${p.code === pincode ? "bg-emerald-50 text-emerald-900" : "text-slate-700"}`}
                  data-testid={`pincode-option-${p.code}`}
                >
                  <span>{p.label}</span>
                  <span className="text-xs font-mono text-slate-400">{p.code}</span>
                </button>
              ))}
            </div>
          </div>
          {current && (
            <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
              Currently showing results near <span className="font-semibold text-slate-700">{current.label}</span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
